import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ScatterChart,
  Scatter,
  Cell,
  ReferenceArea
} from 'recharts';

const FlowStateHeatMap = ({ flowAnalysis }) => {
  // Transform data for visualization
  const stateColors = {
    FLOW: '#2e7d32',         // Darker, more saturated green
    ANXIÉTÉ: '#d32f2f',      // Darker red
    ENNUI: '#1976d2',        // Darker blue
    INDIFFÉRENCE: '#757575', // Darker grey
    EXCITATION: '#d84315',   // Darker orange-red
    MAITRISE: '#2e7d32',     // Darker green
    DETACHEMENT: '#0d47a1',  // Darker blue
    INQUIETUDE: '#c62828',   // Darker red
    NEUTRE: '#757575'        // Darker grey
  };

  const data = flowAnalysis.map(item => ({
    challenge: item.challenge,
    skill: item.skill,
    state: item.state,
    activity: item.activity
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload;
    
    return (
      <Box sx={{ 
        bgcolor: 'background.paper', 
        p: 1, 
        border: '1px solid #ccc',
        borderRadius: 1
      }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {item.activity}
        </Typography>
        <Typography variant="body2">
          État: {item.state}
        </Typography>
        <Typography variant="body2">
          Défi: {(item.challenge * 100).toFixed(0)}%
        </Typography>
        <Typography variant="body2">
          Compétence: {(item.skill * 100).toFixed(0)}%
        </Typography>
      </Box>
    );
  };

  const quadrantColors = {
    // Main states
    FLOW: 'rgba(46, 125, 50, 0.6)',         // Bolder green
    ANXIÉTÉ: 'rgba(211, 47, 47, 0.6)',      // Bolder red
    ENNUI: 'rgba(25, 118, 210, 0.6)',       // Bolder blue
    INDIFFÉRENCE: 'rgba(117, 117, 117, 0.6)', // Bolder grey
    
    // Buffer zones
    DETACHEMENT: 'rgba(13, 71, 161, 0.4)',    // Bolder light blue
    MAITRISE: 'rgba(46, 125, 50, 0.4)',       // Bolder light green
    EXCITATION: 'rgba(216, 67, 21, 0.4)',     // Bolder light red
    INQUIETUDE: 'rgba(198, 40, 40, 0.4)',     // Bolder pink
    
    // Neutral zone
    NEUTRE: 'rgba(117, 117, 117, 0.4)'        // Bolder medium grey
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h6" gutterBottom align="center">
        Distribution des États de Flow sur les 18 activités
      </Typography>
      <Typography 
        variant="body2" 
        align="center" 
        sx={{ 
          fontStyle: 'italic', 
          color: 'text.secondary',
          mb: 1 
        }}
      >
        Survolez les points pour voir le détail des activités
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          >
            <CartesianGrid />
            <XAxis 
              type="number" 
              dataKey="skill" 
              name="Compétence"
              domain={[0, 1]}
              label={{ value: 'Niveau de Compétence', position: 'bottom' }}
            />
            <YAxis 
              type="number" 
              dataKey="challenge" 
              name="Défi"
              domain={[0, 1]}
              label={{ value: 'Niveau de Défi', angle: -90, position: 'left' }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Main quadrants */}
            <ReferenceArea x1={0.66} x2={1} y1={0.66} y2={1} fill={quadrantColors.FLOW} />
            <ReferenceArea x1={0} x2={0.33} y1={0.66} y2={1} fill={quadrantColors.ANXIÉTÉ} />
            <ReferenceArea x1={0.66} x2={1} y1={0} y2={0.33} fill={quadrantColors.ENNUI} />
            <ReferenceArea x1={0} x2={0.33} y1={0} y2={0.33} fill={quadrantColors.INDIFFÉRENCE} />
            
            {/* Buffer zones */}
            <ReferenceArea x1={0.33} x2={0.66} y1={0.66} y2={1} fill={quadrantColors.EXCITATION} />
            <ReferenceArea x1={0.66} x2={1} y1={0.33} y2={0.66} fill={quadrantColors.MAITRISE} />
            <ReferenceArea x1={0.33} x2={0.66} y1={0} y2={0.33} fill={quadrantColors.DETACHEMENT} />
            <ReferenceArea x1={0} x2={0.33} y1={0.33} y2={0.66} fill={quadrantColors.INQUIETUDE} />
            <ReferenceArea x1={0.33} x2={0.66} y1={0.33} y2={0.66} fill="rgba(255, 255, 255, 0.1)" />
            
            {/* Update reference lines to show three sections */}
            <line x1="0.33" y1="0" x2="0.33" y2="1" stroke="#666" strokeDasharray="3 3" />
            <line x1="0.66" y1="0" x2="0.66" y2="1" stroke="#666" strokeDasharray="3 3" />
            <line x1="0" y1="0.33" x2="1" y2="0.33" stroke="#666" strokeDasharray="3 3" />
            <line x1="0" y1="0.66" x2="1" y2="0.66" stroke="#666" strokeDasharray="3 3" />
            
            <Scatter data={data} shape="circle">
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={stateColors[entry.state]} 
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default FlowStateHeatMap;